<script lang="ts">
	import { resolve } from '$app/paths';
</script>

<!-- -->
<!-- Copyright (c) 2025 Cozy Intellect Inc.-->
<!-- File Path: apps/web/src/routes/terms-of-service/+page.svelte-->
<!-- -->
<div class="w-full bg-black">
	<div class="bg-[url('/img/pricingherobg.jpg')] bg-cover bg-no-repeat px-5 pb-32 pt-52">
		<div class="container mx-auto px-5">
			<h1 class="pb-4 text-5xl font-semibold text-w-800 sm:break-normal lg:text-8xl">
				Terms of
				<span class="gd-title">Service</span>
			</h1>
			<p class="pt-4 text-base font-normal text-w-100 lg:text-xl">
				<strong>Effective Date:</strong> May 3rd, 2025
			</p>
		</div>
	</div>

	<div
		class="container prose prose-lg prose-invert mx-auto w-auto bg-black px-5 py-24 lg:max-w-[850px] xl:px-0"
	>
		<h2>Welcome to Retain by CozyIntellect Inc.</h2>

		<div class="px-5git container mx-auto w-auto lg:max-w-[850px] xl:px-0">
			<h2 class="mb-6 text-3xl font-semibold leading-[42px] text-w-800">Terms of Service</h2>

			<p class="mb-6 text-base text-w-100">
				Cozy Intellect Inc. ("Retain," "we," "us," "our") provides its services (described below)
				through its website located at <a
					class="text-blue-500 underline"
					href="https://retainapp.ai"
					target="_blank">https://retainapp.ai</a
				> (the "Site") and through its applications and related services (collectively, such services,
				including any new features and applications, and the Site, the "Service(s)"), subject to the
				following Terms of Service (as amended from time to time, these "Terms of Service"). These Terms
				of Service are effective as of the date you and Retain enter into an online order or you otherwise
				complete the online ordering flow on the Service (the "Effective Date"). If you are accepting
				these Terms of Service on behalf of your employer, organization or another entity (collectively,
				an "Entity") (which will be deemed to the case if you sign up for a Service using an email address
				from such Entity), then you represent and warrant that (a) you have read and understand these
				Terms of Service, (b) you have full legal authority to bind such Entity to these Terms of Service
				and (c) you agree to these Terms of Service on behalf of such Entity. As used in the remainder
				of these Terms of Service, "You" means the Entity you represent in accepting these Terms of Service
				or, if that does not apply, you individually.
			</p>

			<p class="mb-6 text-base text-w-100">
				We reserve the right, at our sole discretion, to change or modify portions of these Terms of
				Service at any time. If we do this, we will post the changes on this page and will indicate
				at the top of this page the date these terms were last revised. We will also notify you,
				either through the Services user interface, in an email notification or through other
				reasonable means. Any such changes will become effective no earlier than fourteen (14) days
				after they are posted, provided that if you have paid for a subscription to a Service
				("Subscription"), then with respect to that Service any such change will take effect at the
				next renewal of your Subscription, except as set forth below. Your continued use of the
				Service after the date any such changes become effective constitutes your acceptance of the
				new Terms of Service.
			</p>

			<h2>1. Eligibility</h2>

			<p class="mb-9 text-base font-normal text-w-100">To use the Service, you must:</p>
			<ul class="ml-6 list-disc text-base text-w-100">
				<li>
					Be at least eighteen (18) years old and legally able to enter into binding contracts;
				</li>
				<li>Complete the account registration process accurately and in full;</li>
				<li>Agree to these Terms and any other terms or policies referenced herein;</li>
				<li>
					Provide and maintain true, complete, and up-to-date contact and billing information;
				</li>
				<li>
					Not reside in or ordinarily be located in any country or region subject to comprehensive
					U.S. embargo or sanctions, including Cuba, Iran, North Korea, Syria, the regions of
					Crimea, Zaporizhzhia, and Kherson, and the DNR and LNR regions of Ukraine, or otherwise be
					prohibited from using the Service under U.S. or other applicable law;
				</li>
				<li>
					Not be listed on any U.S. or applicable jurisdiction government list of prohibited or
					restricted parties, including but not limited to the U.S. Treasury Department's Specially
					Designated Nationals List or the Commerce Department's Entity List.
				</li>
			</ul>

			<p class="mb-6 text-base text-w-100">
				<strong
					>PLEASE READ THESE TERMS OF SERVICE CAREFULLY, AS THEY CONTAIN AN AGREEMENT TO ARBITRATE
					AND OTHER IMPORTANT INFORMATION REGARDING YOUR LEGAL RIGHTS, REMEDIES, AND OBLIGATIONS.
					THE AGREEMENT TO ARBITRATE REQUIRES (WITH LIMITED EXCEPTION) THAT YOU SUBMIT CLAIMS YOU
					HAVE AGAINST US TO BINDING AND FINAL ARBITRATION, AND FURTHER (1) YOU WILL ONLY BE
					PERMITTED TO PURSUE CLAIMS AGAINST RETAIN ON AN INDIVIDUAL BASIS, NOT AS A PLAINTIFF OR
					CLASS MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION OR PROCEEDING, (2) YOU WILL ONLY BE
					PERMITTED TO SEEK RELIEF (INCLUDING MONETARY, INJUNCTIVE, AND DECLARATORY RELIEF) ON AN
					INDIVIDUAL BASIS, AND (3) YOU MAY NOT BE ABLE TO HAVE ANY CLAIMS YOU HAVE AGAINST US
					RESOLVED BY A JURY OR IN A COURT OF LAW.</strong
				>
			</p>

			<p class="mb-6 text-base text-w-100">
				In addition, when using certain services, you will be subject to additional terms applicable
				to such services that may be posted on the Service from time to time, including, without
				limitation, the Acceptable Use Policy located at <a
					class="text-blue-500 underline"
					href="https://retainapp.ai/acceptable-use"
					target="_blank">https://retainapp.ai/acceptable-use</a
				>
				and Privacy Policy located at
				<a
					class="text-blue-500 underline"
					href="https://retainapp.ai/privacy-policy"
					target="_blank">https://retainapp.ai/privacy-policy</a
				>. All such terms are hereby incorporated by reference into these Terms of Service. If you
				are an individual, our Privacy Policy will apply to you and you consent to our collection
				and use of personal data as outlined therein, or if you are an Entity, our Privacy Policy
				will apply to your end users.
			</p>

			<h2>2. Registration and Account Security</h2>
			<p>
				You must provide accurate information when creating your account and are responsible for
				keeping login credentials secure. Notify us immediately of unauthorized use.
			</p>

			<p class="mb-9 text-base text-w-100">
				By using the Service, you represent and warrant that you meet all of the above requirements
				and agree not to use the Service in any way that violates applicable laws or regulations.
				Cozy Intellect Inc. reserves the right to refuse access to the Service, close accounts, or
				revise eligibility criteria at its sole discretion.
			</p>

			<h3 class="mb-9 text-xl font-semibold text-w-800">2.1 Registration:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				To access certain features of the Service, you must register for an account. You agree to
				provide accurate, current, and complete registration information and to promptly update this
				information as necessary. Failure to do so may result in suspension or termination of your
				account.
			</p>

			<h3 class="mb-9 mt-8 text-xl font-semibold text-w-800">2.2 Account Security:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				We implement reasonable technical and organizational measures to help secure your account
				information. However, no system can be guaranteed to be 100% secure. You agree to use best
				practices, including strong passwords and safeguarding your devices, to protect your account
				access.
			</p>

			<h2>3. Use of Services and Restrictions</h2>

			<h3 class="mb-9 text-xl font-semibold text-w-800">3.1 Use of the Services:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				Subject to your compliance with this Agreement, we grant you a limited, non-transferable,
				non-sublicensable, non-exclusive license to access and use the Services for your internal
				business or personal purposes. You may not copy, modify, distribute, sell, or lease any part
				of our Services, nor may you reverse engineer or attempt to extract the source code of the
				software used to provide the Services, unless laws prohibit these restrictions or you have
				our written permission.
			</p>

			<h3 class="mb-9 text-xl font-semibold text-w-800">3.2 Restrictions:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				You agree not to use the Services in any way that:
			</p>
			<ul class="ml-6 list-disc text-base text-w-100">
				<li>Violates any applicable law or regulation;</li>
				<li>
					Infringes upon or misappropriates the intellectual property or other rights of any third
					party;
				</li>
				<li>
					Is harmful, fraudulent, deceptive, threatening, harassing, defamatory, obscene, or
					otherwise objectionable;
				</li>
				<li>Interferes with the operation or security of the Services;</li>
				<li>
					Attempts to gain unauthorized access to other user accounts or Service-related systems;
				</li>
				<li>Attempts to benchmark or replicate the Services in a competing product.</li>
			</ul>
			<p class="mb-9 text-base text-w-100">
				Violations of these restrictions may result in immediate suspension or termination of your
				account, and legal action where applicable.
			</p>

			<h2>4. Third-Party Integrations</h2>
			<h3>4.1. Gmail™ Integration</h3>
			<p>
				If opted-in, RetainApp.AI accesses your Gmail™ account to summarize emails, generate
				drafts, and analyze topics via OpenAI. Email data is securely cached and TLS encrypted. Data
				is shared with OpenAI solely for summarization and embedding.
			</p>

			<p class="mb-9 text-base text-w-100">* Gmail™ is a trademark of Google LLC.</p>

			<h3>4.2. Google Calendar™ Integration</h3>
			<p>
				If opted-in, RetainApp.AI fetches calendar events to display events, join meetings, and
				associate notes. Events are accessed with read-only permissions, cached locally, and never
				stored on our servers.
			</p>

			<p class="mb-9 text-base text-w-100">* Google Calendar™ is a trademark of Google LLC.</p>

			<h2>5. Fees and Payment</h2>
			<p>
				Pricing is provided on our website. You agree to timely payment and acknowledge pricing can
				change with notice. You are responsible for applicable taxes.
			</p>

			<h2>6. Feedback</h2>
			<p>
				We value your feedback and suggestions regarding our Services. By submitting feedback, you
				grant Cozy Intellect Inc. a worldwide, non-exclusive, perpetual, irrevocable, royalty-free,
				fully paid license to use, reproduce, modify, create derivative works from, distribute,
				publicly display, and otherwise exploit your feedback in any manner without restriction.
			</p>

			<h2>7. Confidentiality and Data Protection</h2>
			<p>
				You agree to maintain confidentiality of non-public information from CozyIntellect. We
				handle your personal data as outlined in our <a
					class="text-blue-500 underline"
					href={resolve('/privacy-policy')}
					target="_blank">Privacy Policy</a
				>.
			</p>
			<h2 class="mb-6 mt-12 text-3xl font-semibold leading-[42px] text-w-800">
				8. Fees and Payment
			</h2>
			<h3 class="mb-9 text-xl font-semibold text-w-800">8.1 Pricing and Billing:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				Fees for the Services are described on our website or in a separate ordering document. You
				agree to pay all applicable fees in accordance with the billing terms in effect at the time
				the fee becomes payable. We reserve the right to change our pricing by providing advance
				notice via the Service or to your account email. Continued use of the Service after such
				changes become effective constitutes your agreement to the new pricing.
			</p>
			<p class="mb-9 text-base font-normal text-w-100">
				All payments must be made using a valid payment method accepted by us. If your payment
				method fails or your account is past due, we may suspend or terminate your access to the
				Services. You agree to provide current, complete, and accurate billing information, and to
				promptly update such information if it changes.
			</p>
			<h3 class="mb-9 text-xl font-semibold text-w-800">8.2 Taxes:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				You are responsible for any duties, customs fees, or taxes (other than our income tax)
				associated with the sale or use of the Services, including any related penalties or
				interest. If we are required to collect or pay any taxes on your behalf, you agree to pay
				the amount invoiced to you.
			</p>

			<h2>9. Intellectual Property</h2>
			<p>
				All intellectual property rights in RetainApp.AI Services are owned by CozyIntellect Inc. or
				licensors. No transfer of rights occurs under this Agreement.
			</p>

			<h2 class="mb-6 text-3xl font-semibold leading-[42px] text-w-800">10. Use of AI Features</h2>

			<p class="mb-6 text-base text-w-100">
				Retain may offer or integrate artificial intelligence features within the Service (each, an
				“AI Model”) to support your productivity, including helping you summarize content, draft
				communications, generate insights, and search across your data. These AI Models are
				considered part of the Service.
			</p>

			<p class="mb-6 text-base text-w-100">
				You are solely responsible for any data, instructions, prompts, text, or other material that
				you provide when using these features ("Inputs"), and for reviewing any resulting content
				("Outputs"). You acknowledge and agree that Outputs may be inaccurate, non-unique, or not
				suitable for use without further review or modification. Outputs should not be relied upon
				as legal, business, or professional advice, and you are solely responsible for validating
				their accuracy and fitness for your purpose.
			</p>

			<p class="mb-6 text-base text-w-100">
				Outputs may not be eligible for copyright or intellectual property protection and may be
				similar or identical to content generated for other users. You are not required to use any
				Outputs and should modify or discard them as appropriate.
			</p>

			<p class="mb-6 text-base text-w-100">
				Retain leverages third-party AI service providers including <a
					class="text-blue-500 underline"
					href="https://openai.com/enterprise-privacy/"
					target="_blank">OpenAI</a
				>,
				<a
					class="text-blue-500 underline"
					href="https://www.anthropic.com/legal/terms-of-service"
					target="_blank">Anthropic</a
				>, and
				<a
					class="text-blue-500 underline"
					href="https://ai.google.dev/gemini-api/terms"
					target="_blank">Google Gemini API</a
				>. These providers may process and store Inputs and Outputs in order to deliver the
				AI-powered functionality within the Service. By using the AI features, you authorize the use
				of these third-party processors under their applicable terms and privacy policies.
			</p>

			<p class="mb-6 text-base text-w-100">
				Cozy Intellect Inc. does <strong>not</strong> permit the use of your Inputs or Outputs by third-party
				AI providers for the training or development of generalized machine learning models. Additionally,
				we exclusively use paid versions of these AI services where applicable, such as the Google Gemini
				API Paid Services, which ensures your data is not used to improve the provider's products.
			</p>

			<p class="mb-6 text-base text-w-100">
				The availability, scope, and quality of AI Models may vary and may be limited or
				discontinued at any time without notice. You agree to use AI features only in compliance
				with these Terms and our Acceptable Use Policy.
			</p>

			<h2>11. Disclaimer of Warranties</h2>
			<p>
				The Services are provided "as is" without warranties of any kind. CozyIntellect disclaims
				all implied warranties, including merchantability or fitness for a particular purpose.
			</p>

			<h2>12. Limitation of Liability</h2>
			<p>
				CozyIntellect's total liability under this Agreement is limited to the amount you paid for
				the Services in the 12 months preceding any claim.
			</p>
			<h2 class="mb-6 mt-12 text-3xl font-semibold leading-[42px] text-w-800">
				13. Confidentiality and Data Protection
			</h2>

			<h3 class="mb-9 text-xl font-semibold text-w-800">13.1 Confidentiality:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				You agree to maintain in confidence any non-public, confidential, or proprietary information
				disclosed by Cozy Intellect Inc. in connection with the Services, and to use such
				information solely for the purpose of fulfilling your obligations under this Agreement. This
				obligation does not apply to information that is publicly available, independently developed
				without use of confidential information, or lawfully obtained from a third party without
				restriction.
			</p>

			<h3 class="mb-9 text-xl font-semibold text-w-800">13.2 Data Protection:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				Cozy Intellect Inc. processes personal data in accordance with applicable privacy and data
				protection laws, as further described in our <a
					class="text-blue-500 underline"
					href={resolve('/privacy-policy')}
					target="_blank">Privacy Policy</a
				>. In most instances, Cozy Intellect Inc. acts as a processor or service provider on your
				behalf, processing personal data only in accordance with your instructions, for example in
				relation to your content or workspace usage. In certain instances, such as where necessary
				for billing, account management, analytics, product development, and platform security, Cozy
				Intellect Inc. may act as an independent controller.
			</p>

			<h2>14. Indemnification</h2>
			<p>
				You agree to indemnify CozyIntellect against claims arising from your breach of this
				Agreement or misuse of the Services.
			</p>

			<h2 class="mb-6 mt-12 text-3xl font-semibold leading-[42px] text-w-800">
				15. Dispute Resolution
			</h2>
			<h3 class="mb-9 text-xl font-semibold text-w-800">15.1 Governing Law:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				This Agreement is governed by the laws of the State of Delaware, USA.
			</p>
			<h3 class="mb-9 text-xl font-semibold text-w-800">15.2 Arbitration:</h3>
			<p class="mb-9 text-base font-normal text-w-100">
				Disputes shall be resolved through binding arbitration in accordance with the rules of the
				American Arbitration Association.
			</p>

			<h2>16. Changes to This Agreement</h2>
			<p>
				We may update this Agreement periodically and will notify you of significant changes.
				Continued use of the Services constitutes acceptance of such changes.
			</p>
			<p class="mb-9 text-base font-normal text-w-100">
				We implement reasonable and appropriate technical and organizational safeguards to protect
				your personal data against unauthorized access, use, loss, or disclosure. These measures
				include secure data transmission via TLS, encryption of data at rest, role-based access
				controls, and regular security assessments.
			</p>
			<p class="mb-9 text-base font-normal text-w-100">
				We may use your telephone number or other contact details to provide multi-factor
				authentication (MFA) for account security. You agree to receive verification codes via SMS
				or other secure channels as part of this process. Message and data rates may apply. You may
				opt out of non-essential communications at any time, but opting out of security-related
				messages may affect your use of the Services.
			</p>
			<p class="mb-9 text-base font-normal text-w-100">
				We may also use your contact details to send you communications about critical product
				updates, security notices, support responses, and optional promotional content. You may
				control your communication preferences or opt out of marketing messages by using the
				unsubscribe link provided in such emails or contacting us at <a
					class="text-blue-500 underline"
					href="mailto:support@retainapp.ai">support@retainapp.ai</a
				>.
			</p>
			<p class="mb-9 text-base font-normal text-w-100">
				Your consent to receive automated communications is voluntary and may be withdrawn at any
				time. However, withdrawing consent to receive critical notices may limit our ability to
				deliver certain Services to you. We are not liable for any charges imposed by your
				telecommunications provider for such communications.
			</p>

			<h2>17. Contact Information</h2>
			<p>For questions regarding this Agreement, contact us at:</p>
			<address>
				CozyIntellect Inc.<br />
				Email: <a href="mailto:admin@cozyintellect.com">admin@cozyintellect.com</a>
			</address>

			<p>
				<strong>Trademark Notice:</strong> Gmail and Google Calendar are trademarks of Google LLC. All
				rights reserved. Use of these trademarks is subject to Google's permissions and applicable laws.
			</p>

			<p>Thank you for choosing RetainApp.AI.</p>
		</div>
	</div>
</div>
